import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE } from "../api/config";
import "./TransferDiscrepancyControlPanel.css";
import { showOfficeConfirm, showOfficePrompt } from "../utils/officeActionDialog";
// EXTRA_ITEM_OFFICE_ASSIGN_UI_V1
// EXTRA_ITEM_ASSIGN_SOURCE_DROPDOWN_V1
// EXTRA_ITEM_ASSIGN_INCLUDE_WAREHOUSE_V1

const STATUS_OPTIONS = [
  { value: "ALL", label: "All Status" },
  { value: "OPEN", label: "Open" },
  { value: "NEED_OFFICE_REVIEW", label: "Need Review" },
  { value: "WRONG_ITEM_REPORTED", label: "Wrong Item Reported" },
  { value: "OFFICE_REVIEW_PENDING", label: "Office Review" },
  { value: "OFFICE_REJECTED_RETURN_KEEP_RECEIVER", label: "Keep at Receiver" },
  { value: "OFFICE_APPROVED_RETURN", label: "Return Approved" },
  { value: "RETURN_ASSIGNED_TO_DELIVERY", label: "Delivery Assigned" },
  { value: "RETURN_SENT_TO_DELIVERY", label: "Return Sent" },
  { value: "RETURN_RECEIVED_CLOSED", label: "Return Received / Closed" },
  { value: "RETURN_CANCELLED_BY_RECEIVER", label: "Cancelled by Receiver" },
  { value: "RETURN_DISPUTED_NOT_RECEIVED", label: "Not Received" },
  { value: "RETURN_IN_TRANSIT", label: "Return In Transit" },
  { value: "APPROVED_KEEP_COMPLETED", label: "Keep Approved" },
  { value: "EXTRA_ITEM_PENDING_SENDER_DECISION", label: "Extra Item: Sender Decision" },
  { value: "EXTRA_ITEM_REJECTED_WAITING_OFFICE_ASSIGN", label: "Extra Item: Office Assign" },
  { value: "EXTRA_ITEM_CLOSED_BY_SENDER_APPROVAL", label: "Extra Item: Sender Closed" },
  { value: "EXTRA_ITEM_CLOSED_BY_OFFICE_ASSIGN", label: "Extra Item: Assigned Closed" },
];

const TYPE_OPTIONS = [
  { value: "ALL", label: "All Types" },
  { value: "EXTRA_ITEM", label: "Extra Item" },
  { value: "WRONG_SIZE", label: "Wrong Size" },
  { value: "WRONG_ITEM", label: "Wrong Item" },
  { value: "MISSING_ITEM", label: "Missing Item" },
  { value: "QTY_SHORT", label: "Qty Short" },
  { value: "QTY_OVER", label: "Qty Over" },
  { value: "DAMAGED_ITEM", label: "Damaged Item" },
];

function norm(value) {
  return String(value || "").trim().toUpperCase();
}

function shortId(value) {
  return String(value || "").slice(0, 8);
}

function formatDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function fmtQty(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0";
  return n % 1 === 0 ? String(n) : n.toFixed(2);
}

function typeLabel(type) {
  const t = norm(type);
  if (t === "EXTRA_ITEM") return "Extra Item";
  if (t === "WRONG_SIZE") return "Wrong Size";
  if (t === "WRONG_ITEM") return "Wrong Item";
  if (t === "MISSING_ITEM") return "Missing Item";
  if (t === "QTY_SHORT") return "Qty Short";
  if (t === "QTY_OVER") return "Qty Over";
  if (t === "DAMAGED_ITEM") return "Damaged Item";
  return t || "-";
}

function actionLabel(action) {
  const a = norm(action);
  if (a === "REPORT_ONLY") return "Report Only";
  if (a === "RETURN_LATER") return "Return Request";
  if (a === "KEEP_REQUEST_APPROVAL") return "Keep Request";
  if (a === "EXTRA_ITEM_REPORTED") return "Extra item reported";
  if (a === "KEEP_WRONG_ITEM_APPROVAL") return "Keep Wrong Item";
  if (a === "KEEP_WRONG_SIZE_APPROVAL") return "Keep Wrong Size";
  if (a === "RETURN_TO_SENDER") return "Return to Sender";
  if (a === "RETURN_TO_CENTER") return "Return to Center";
  if (a === "RETURN_TO_WAREHOUSE") return "Return to Warehouse";
  return a || "-";
}

function statusLabel(status) {
  const s = norm(status);
  if (s === "OPEN") return "Open";
  if (s === "NEED_OFFICE_REVIEW") return "Need Review";
  if (s === "EXTRA_ITEM_REPORTED") return "Extra Item Reported";
  if (s === "EXTRA_ITEM_PENDING_SENDER_DECISION") return "Waiting Sender Decision";
  if (s === "EXTRA_ITEM_REJECTED_WAITING_OFFICE_ASSIGN") return "Waiting Office Assign";
  if (s === "EXTRA_ITEM_CLOSED_BY_SENDER_APPROVAL") return "Closed by Sender Approval";
  if (s === "EXTRA_ITEM_CLOSED_BY_OFFICE_ASSIGN") return "Closed by Office Assign";
  if (s === "OFFICE_REVIEW_PENDING") return "Office Review";
  if (s === "SENDER_CONFIRM_PENDING") return "Waiting for Sender Confirm";
  if (s === "SENDER_CONFIRMED_EXTRA_SENT") return "Sender Confirmed";
  if (s === "SENDER_REJECTED_EXTRA_ITEM") return "Sender Rejected / Review";
  if (s === "EXTRA_ITEM_APPROVED_STOCK_POSTED") return "Stock Posted / Closed";
  if (s === "EXTRA_ITEM_REJECTED_CLOSED") return "Rejected / Closed";
  if (s === "OFFICE_APPROVED_RETURN") return "Return Approved";
  if (s === "RETURN_ASSIGNED_TO_DELIVERY") return "Delivery Assigned";
  if (s === "RETURN_SENT_TO_DELIVERY") return "Return Sent";
  if (s === "RETURN_RECEIVED_CLOSED") return "Return Received / Closed";
  if (s === "RETURN_DISPUTED_NOT_RECEIVED") return "Not Received / Review";
  if (s === "APPROVED_WRONG_ITEM_KEEP_COMPLETED") return "Wrong Item Keep Approved";
  return s.replaceAll("_", " ") || "-";
}

function getExpectedText(row) {
  const code = row.expected_product_code || "-";
  const color = row.expected_color || "-";
  const size = row.expected_size || "-";
  const qty = fmtQty(row.expected_qty);
  return `${code} / ${color} / ${size} / Qty ${qty}`;
}

function getReceivedText(row) {
  const code = row.received_product_code || "-";
  const color = row.received_color || "-";
  const size = row.received_size || "-";
  const qty = fmtQty(row.received_qty);
  return `${code} / ${color} / ${size} / Qty ${qty}`;
}

function canApproveExtraKeep(row) {
  return false;
}

function canApproveConfirmedExtraItem(row) {
  return (
    norm(row.discrepancy_type) === "EXTRA_ITEM" &&
    norm(row.status) === "SENDER_CONFIRMED_EXTRA_SENT"
  );
}

function canRejectExtraItemClose(row) {
  return (
    norm(row.discrepancy_type) === "EXTRA_ITEM" &&
    [
      "OPEN",
      "NEED_OFFICE_REVIEW",
      "EXTRA_ITEM_REPORTED",
      "OFFICE_REVIEW_PENDING",
      "SENDER_CONFIRM_PENDING",
      "SENDER_CONFIRMED_EXTRA_SENT",
      "SENDER_REJECTED_EXTRA_ITEM",
    ].includes(norm(row.status))
  );
}

function isExtraItemWaitingSender(row) {
  return (
    norm(row.discrepancy_type) === "EXTRA_ITEM" &&
    [
      "OPEN",
      "NEED_OFFICE_REVIEW",
      "EXTRA_ITEM_REPORTED",
      "OFFICE_REVIEW_PENDING",
      "SENDER_CONFIRM_PENDING",
    ].includes(norm(row.status))
  );
}

function isExtraItemClosed(row) {
  return (
    norm(row.discrepancy_type) === "EXTRA_ITEM" &&
    [
      "EXTRA_ITEM_APPROVED_STOCK_POSTED",
      "EXTRA_ITEM_REJECTED_CLOSED",
    ].includes(norm(row.status))
  );
}

function canOfficeAssignExtraSource(row) {
  return (
    norm(row.discrepancy_type) === "EXTRA_ITEM" &&
    norm(row.status) === "EXTRA_ITEM_REJECTED_WAITING_OFFICE_ASSIGN"
  );
}

function canOfficeClosedExtraItem(row) {
  return (
    norm(row.discrepancy_type) === "EXTRA_ITEM" &&
    [
      "EXTRA_ITEM_CLOSED_BY_SENDER_APPROVAL",
      "EXTRA_ITEM_CLOSED_BY_OFFICE_ASSIGN",
    ].includes(norm(row.status))
  );
}

function canApproveWrongItemKeep(row) {
  return (
    norm(row.discrepancy_type) === "WRONG_ITEM" &&
    ["KEEP_REQUEST_APPROVAL", "KEEP_WRONG_ITEM_APPROVAL"].includes(norm(row.requested_action)) &&
    ["OPEN", "NEED_OFFICE_REVIEW"].includes(norm(row.status))
  );
}

function canReturnLater(row) {
  return (
    norm(row.discrepancy_type) !== "WRONG_ITEM" &&
    norm(row.requested_action) === "RETURN_LATER" &&
    ["OPEN", "NEED_OFFICE_REVIEW"].includes(norm(row.status))
  );
}

function canDecideWrongItemReturn(row) {
  return (
    norm(row.discrepancy_type) === "WRONG_ITEM" &&
    norm(row.requested_action) === "RETURN_LATER" &&
    [
      "OPEN",
      "NEED_OFFICE_REVIEW",
      "WRONG_ITEM_REPORTED",
      "OFFICE_REVIEW_PENDING",
    ].includes(norm(row.status))
  );
}

function isWrongItemReturnWaiting(row) {
  return (
    norm(row.discrepancy_type) === "WRONG_ITEM" &&
    [
      "OFFICE_APPROVED_RETURN",
      "RETURN_ASSIGNED_TO_DELIVERY",
      "RETURN_SENT_TO_DELIVERY",
      "RETURN_DISPUTED_NOT_RECEIVED",
    ].includes(norm(row.status))
  );
}

function isWrongItemReturnClosed(row) {
  return (
    norm(row.discrepancy_type) === "WRONG_ITEM" &&
    [
      "OFFICE_REJECTED_RETURN_KEEP_RECEIVER",
      "RETURN_RECEIVED_CLOSED",
      "RETURN_CANCELLED_BY_RECEIVER",
      "APPROVED_WRONG_ITEM_KEEP_COMPLETED",
    ].includes(norm(row.status))
  );
}

function canConfirmReturn(row) {
  return norm(row.status) === "RETURN_IN_TRANSIT";
}

function canDecideWrongSize(row) {
  return (
    norm(row.discrepancy_type) === "WRONG_SIZE" &&
    ["OPEN", "NEED_OFFICE_REVIEW"].includes(norm(row.status))
  );
}

function canWrongSizeReturnSent(row) {
  return (
    norm(row.discrepancy_type) === "WRONG_SIZE" &&
    [
      "WRONG_SIZE_RETURN_TO_SENDER_APPROVED",
      "WRONG_SIZE_RETURN_TO_CENTER_APPROVED",
      "WRONG_SIZE_RETURN_TO_WAREHOUSE_APPROVED",
    ].includes(norm(row.status))
  );
}

function canWrongSizeConfirmReceived(row) {
  return (
    norm(row.discrepancy_type) === "WRONG_SIZE" &&
    norm(row.status) === "WRONG_SIZE_RETURN_IN_TRANSIT"
  );
}

function wrongSizeReturnNeedsDestination(row) {
  const decision = norm(row.office_decision);
  return decision === "RETURN_TO_CENTER" || decision === "RETURN_TO_WAREHOUSE";
}

export default function TransferDiscrepancyControlPanel() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("ALL");
  const [type, setType] = useState("ALL");
  const [shop, setShop] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState("");
  const [assignSourceMap, setAssignSourceMap] = useState({});
  const [shopOptions, setShopOptions] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [expandedId, setExpandedId] = useState("");

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams();
      params.set("status", status || "ALL");
      params.set("type", type || "ALL");
      params.set("limit", "100");
      if (shop && shop !== "ALL") params.set("shop", shop);

      const res = await fetch(
        `${API_BASE}/api/erp/pos/transfer-discrepancies?${params.toString()}`
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to load transfer discrepancies");
      }

      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to load transfer discrepancies");
    } finally {
      setLoading(false);
    }
  }, [status, type, shop]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const loadShopOptions = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/erp/location-master`, {
        cache: "no-store",
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Load locations failed");
      }

      const rawRows = Array.isArray(data.rows)
        ? data.rows
        : Array.isArray(data.locations)
        ? data.locations
        : Array.isArray(data)
        ? data
        : [];

      const blockedLocationCodes = new Set(["AYM"]);

      const shops = rawRows
        .map((r) => {
          const code = norm(
            r.location_code ||
              r.locationCode ||
              r.shop_code ||
              r.shopCode ||
              r.code ||
              r.name ||
              ""
          );

          const name = String(
            r.location_name ||
              r.locationName ||
              r.shop_name ||
              r.shopName ||
              r.name ||
              code
          ).trim();

          const type = norm(
            r.location_type ||
              r.locationType ||
              r.type ||
              r.category ||
              ""
          );

          const active =
            r.is_active === undefined &&
            r.active === undefined &&
            r.status === undefined
              ? true
              : r.is_active === true ||
                r.active === true ||
                norm(r.status) === "ACTIVE";

          return { code, name, type, active };
        })
        .filter((r) => {
          if (!r.code) return false;
          if (blockedLocationCodes.has(r.code)) return false;
          if (r.active === false) return false;
          return true;
        });

      const unique = Array.from(
        new Map(shops.map((s) => [s.code, s])).values()
      ).sort((a, b) => a.code.localeCompare(b.code));

      setShopOptions(unique);
    } catch (err) {
      console.error("Load shop options failed:", err);
      setShopOptions([]);
    }
  }, []);

  useEffect(() => {
    loadShopOptions();
  }, [loadShopOptions]);

  const stats = useMemo(() => {
    const loaded = rows.length;
    const open = rows.filter((r) => norm(r.status) === "OPEN").length;
    const needReview = rows.filter((r) => norm(r.status) === "NEED_OFFICE_REVIEW").length;
    const wrongSize = rows.filter((r) => norm(r.discrepancy_type) === "WRONG_SIZE").length;
    const keepApproval = rows.filter((r) =>
      ["KEEP_REQUEST_APPROVAL", "KEEP_WRONG_SIZE_APPROVAL"].includes(norm(r.requested_action))
    ).length;

    return { loaded, open, needReview, wrongSize, keepApproval };
  }, [rows]);

  async function approveWrongItemKeep(row) {
    const ok = await showOfficeConfirm(
      "Approve keeping this WRONG ITEM at receiver?\n\nExpected item will be restored to sender. Received wrong item will move from sender OUT to receiver IN."
    );

    if (!ok) return;

    const note = await showOfficePrompt(
      "Office approval note",
      "Office checked and approved receiver to keep wrong item"
    );

    if (note === null) return;

    try {
      setActionLoadingId(row.id);
      setError("");
      setSuccess("");

      const res = await fetch(
        `${API_BASE}/api/erp/office/transfer-discrepancy/wrong-item-approve-keep`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            discrepancyId: row.id,
            approvedBy: "OFFICE",
            approvalNote: note,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Wrong item approve keep failed");
      }

      setSuccess(data.message || "Wrong item keep approved.");
      await loadRows();
    } catch (err) {
      console.error(err);
      setError(err.message || "Wrong item approve keep failed");
    } finally {
      setActionLoadingId("");
    }
  }

  async function decideWrongItemReturn(row, decision) {
    const isApproveReturn = decision === "APPROVE_RETURN";

    const confirmMessage = isApproveReturn
      ? "Approve return request?\n\nSender expected item will be restored.\nSender actual wrong item will be moved to return process.\nReceiver stock will not increase yet."
      : "Reject return request?\n\nReceiver will keep the wrong item.\nSender expected item will be restored.\nSender actual wrong item will be deducted.\nReceiver stock will increase by 1."; 

    const ok = await showOfficeConfirm(confirmMessage, {
      title: isApproveReturn ? "Approve Return" : "Reject Return / Keep at Receiver",
      okText: isApproveReturn ? "Approve Return" : "Keep at Receiver",
      danger: !isApproveReturn,
    });

    if (!ok) return;

    let deliveryStaff = "";
    let deliveryNote = "";

    if (isApproveReturn) {
      deliveryStaff = await showOfficePrompt(
        "Delivery staff name. Example: Mg Mg",
        row.delivery_staff || "Mg Mg",
        { title: "Assign Delivery" }
      );

      if (deliveryStaff === null) return;

      deliveryStaff = String(deliveryStaff || "").trim();

      if (!deliveryStaff) {
        setError("Delivery staff name is required.");
        return;
      }

      deliveryNote = await showOfficePrompt(
        "Delivery note / route",
        `${row.to_location_code || "Receiver"} -> ${row.from_location_code || "Sender"} return delivery`,
        { title: "Delivery Note" }
      );

      if (deliveryNote === null) return;
    }

    const note = await showOfficePrompt(
      "Office note",
      isApproveReturn
        ? "Office approved wrong item return"
        : "Office rejected return. Receiver keeps wrong item",
      { title: "Office Note" }
    );

    if (note === null) return;

    try {
      setActionLoadingId(row.id);
      setError("");
      setSuccess("");

      const res = await fetch(
        `${API_BASE}/api/erp/office/transfer-discrepancy/wrong-item-return-decision`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            discrepancyId: row.id,
            decision,
            approvedBy: "OFFICE",
            note,
            deliveryStaff,
            deliveryNote,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Wrong item return decision failed");
      }

      setSuccess(
        data.message ||
          (isApproveReturn
            ? "Return approved and delivery assigned."
            : "Return rejected. Receiver will keep the wrong item.")
      );

      await loadRows();
    } catch (err) {
      console.error(err);
      setError(err.message || "Wrong item return decision failed");
    } finally {
      setActionLoadingId("");
    }
  }

  
  async function approveConfirmedExtraItem(row) {
    const ok = await showOfficeConfirm(
      "Approve confirmed extra item?\n\nSender stock will be deducted.\nReceiver stock will be increased.",
      {
        title: "Approve Extra Item",
        okText: "Approve Extra Item",
      }
    );

    if (!ok) return;

    const note = await showOfficePrompt(
      "Office note",
      "Office approved confirmed extra item"
    );

    if (note === null) return;

    try {
      setActionLoadingId(row.id);
      setError("");
      setSuccess("");

      const res = await fetch(
        `${API_BASE}/api/erp/office/transfer-discrepancy/extra-item-approve-confirmed`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            discrepancyId: row.id,
            approvedBy: "OFFICE",
            note,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Approve extra item failed");
      }

      setSuccess(data.message || "Extra item approved. Stock posted.");
      await loadRows();
    } catch (err) {
      console.error(err);
      setError(err.message || "Approve extra item failed");
    } finally {
      setActionLoadingId("");
    }
  }

  async function rejectExtraItemClose(row) {
    const ok = await showOfficeConfirm(
      "Reject and close this extra item request?\n\nNo stock movement will be posted.",
      {
        title: "Reject Extra Item",
        okText: "Reject / Close",
        danger: true,
      }
    );

    if (!ok) return;

    const reason = await showOfficePrompt(
      "Reject reason",
      "Office rejected extra item request"
    );

    if (reason === null) return;

    try {
      setActionLoadingId(row.id);
      setError("");
      setSuccess("");

      const res = await fetch(
        `${API_BASE}/api/erp/office/transfer-discrepancy/extra-item-reject-close`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            discrepancyId: row.id,
            rejectedBy: "OFFICE",
            reason,
            note: reason,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Reject extra item failed");
      }

      setSuccess(data.message || "Extra item rejected and closed.");
      await loadRows();
    } catch (err) {
      console.error(err);
      setError(err.message || "Reject extra item failed");
    } finally {
      setActionLoadingId("");
    }
  }


  async function approveExtraKeep(row) {
    const ok = await showOfficeConfirm(
      "Approve keeping this extra item at receiver?\nSender OUT and receiver IN will be posted."
    );
    if (!ok) return;

    try {
      setActionLoadingId(row.id);
      setError("");
      setSuccess("");

      const res = await fetch(`${API_BASE}/api/erp/office/transfer-discrepancy/approve-keep`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          discrepancyId: row.id,
          approvedBy: "OFFICE",
          approvalNote: "Approved from Transfer Error Control",
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Approve keep failed");
      }

      setSuccess(data.message || "Approved keep completed.");
      await loadRows();
    } catch (err) {
      console.error(err);
      setError(err.message || "Approve keep failed");
    } finally {
      setActionLoadingId("");
    }
  }

  async function markReturnSent(row) {
    const carrier = await showOfficePrompt("Return carrier / delivery route", "Road / delivery later");
    if (carrier === null) return;

    const note = await showOfficePrompt("Return note", "Returned when delivery route was available");
    if (note === null) return;

    try {
      setActionLoadingId(row.id);
      setError("");
      setSuccess("");

      const res = await fetch(`${API_BASE}/api/erp/pos/transfer-discrepancy/return-sent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          discrepancyId: row.id,
          returnSentBy: row.reported_by_location || row.to_location_code || "POS",
          returnCarrier: carrier,
          returnNote: note,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Return sent failed");
      }

      setSuccess(data.message || "Return marked as sent.");
      await loadRows();
    } catch (err) {
      console.error(err);
      setError(err.message || "Return sent failed");
    } finally {
      setActionLoadingId("");
    }
  }

  async function decideWrongSize(row, decision) {
    const decisionLabel =
      decision === "APPROVE_KEEP_WRONG_SIZE"
        ? "Approve receiver to keep wrong size"
        : decision === "RETURN_TO_SENDER"
        ? "Return wrong size item to sender"
        : decision === "RETURN_TO_CENTER"
        ? "Return wrong size item to center"
        : decision === "RETURN_TO_WAREHOUSE"
        ? "Return wrong size item to warehouse"
        : decision;

    const ok = await showOfficeConfirm(
      decisionLabel +
        "?\n\nExpected item will be restored to sender.\nStock movement will be posted only according to this decision."
    );

    if (!ok) return;

    const note = await showOfficePrompt(
      "Office decision note",
      decisionLabel + " approved by office"
    );

    if (note === null) return;

    let returnDestination = "";
    if (decision === "RETURN_TO_SENDER") returnDestination = "SENDER";
    if (decision === "RETURN_TO_CENTER") returnDestination = "CENTER";
    if (decision === "RETURN_TO_WAREHOUSE") returnDestination = "WAREHOUSE";

    try {
      setActionLoadingId(row.id);
      setError("");
      setSuccess("");

      const res = await fetch(
        `${API_BASE}/api/erp/office/transfer-discrepancy/wrong-size-decision`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            discrepancyId: row.id,
            decision,
            returnDestination,
            approvedBy: "OFFICE",
            decisionNote: note,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Wrong size office decision failed");
      }

      setSuccess(data.message || "Wrong size office decision completed.");
      await loadRows();
    } catch (err) {
      console.error(err);
      setError(err.message || "Wrong size office decision failed");
    } finally {
      setActionLoadingId("");
    }
  }

  async function markWrongSizeReturnSent(row) {
    const ok = await showOfficeConfirm(
      "Mark wrong size item as RETURN SENT?\n\nThis will not post stock movement yet. It only changes status to in-transit."
    );

    if (!ok) return;

    const carrier = await showOfficePrompt(
      "Return carrier / delivery route",
      row.return_carrier || "Road / delivery later"
    );

    if (carrier === null) return;

    const note = await showOfficePrompt(
      "Return sent note",
      "Wrong size item returned after office decision"
    );

    if (note === null) return;

    try {
      setActionLoadingId(row.id);
      setError("");
      setSuccess("");

      const res = await fetch(
        `${API_BASE}/api/erp/office/transfer-discrepancy/wrong-size-return-sent`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            discrepancyId: row.id,
            returnSentBy: row.reported_by_location || row.to_location_code || "POS",
            returnCarrier: carrier,
            returnNote: note,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Wrong size return sent failed");
      }

      setSuccess(data.message || "Wrong size return marked as sent.");
      await loadRows();
    } catch (err) {
      console.error(err);
      setError(err.message || "Wrong size return sent failed");
    } finally {
      setActionLoadingId("");
    }
  }

  async function confirmWrongSizeReturnReceived(row) {
    let destinationLocation = "";

    if (wrongSizeReturnNeedsDestination(row)) {
      const currentDestination = norm(row.return_destination);
      const defaultValue =
        currentDestination &&
        !["CENTER", "WAREHOUSE", "SENDER"].includes(currentDestination)
          ? currentDestination
          : "";

      const input = await showOfficePrompt(
        "Real destination location code required. Example: DNGHT, DNGPF, AYMHT, AYMPF",
        defaultValue || "DNGHT"
      );

      if (input === null) return;

      destinationLocation = norm(input);

      if (!destinationLocation || ["CENTER", "WAREHOUSE", "SENDER"].includes(destinationLocation)) {
        setError("Please enter real destination location code, not CENTER / WAREHOUSE text.");
        return;
      }
    }

    const note = await showOfficePrompt(
      "Received note",
      wrongSizeReturnNeedsDestination(row)
        ? "Destination confirmed wrong size item received"
        : "Sender confirmed wrong size item received"
    );

    if (note === null) return;

    try {
      setActionLoadingId(row.id);
      setError("");
      setSuccess("");

      const res = await fetch(
        `${API_BASE}/api/erp/office/transfer-discrepancy/wrong-size-return-received`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            discrepancyId: row.id,
            receivedBy: destinationLocation || row.from_location_code || "SENDER",
            destinationLocation,
            receivedNote: note,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Wrong size return received failed");
      }

      setSuccess(data.message || "Wrong size return received and closed.");
      await loadRows();
    } catch (err) {
      console.error(err);
      setError(err.message || "Wrong size return received failed");
    } finally {
      setActionLoadingId("");
    }
  }

  async function confirmReturnReceived(row) {
    const note = await showOfficePrompt("Received note", "Sender confirmed physical return received");
    if (note === null) return;

    try {
      setActionLoadingId(row.id);
      setError("");
      setSuccess("");

      const res = await fetch(`${API_BASE}/api/erp/pos/transfer-discrepancy/return-received`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          discrepancyId: row.id,
          receivedBy: row.from_location_code || "SENDER",
          receivedNote: note,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Confirm received failed");
      }

      setSuccess(data.message || "Return received and closed.");
      await loadRows();
    } catch (err) {
      console.error(err);
      setError(err.message || "Confirm received failed");
    } finally {
      setActionLoadingId("");
    }
  }

  async function assignExtraItemSource(row, selectedShop = "") {
    const cleanShop = norm(
      selectedShop ||
        assignSourceMap[row.id] ||
        row.office_assigned_source_location ||
        ""
    );

    if (!cleanShop) {
      setError("Please select assigned source location first.");
      return;
    }

    const note = await showOfficePrompt(
      "Office assign note",
      "Office checked and assigned actual source shop to " + cleanShop
    );

    if (note === null) return;

    const ok = await showOfficeConfirm(
      "Assign extra item source to " +
        cleanShop +
        "?\n\nThis will post Assigned Shop inventory -1. Receiver stock will NOT be reversed."
    );

    if (!ok) return;

    try {
      setActionLoadingId(row.id);
      setError("");
      setSuccess("");

      const res = await fetch(
        `${API_BASE}/api/erp/office/transfer-discrepancy/extra-item-assign-source`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            discrepancyId: row.id,
            assignedSourceShop: cleanShop,
            assignedBy: "office_admin",
            note,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Assign source shop failed");
      }

      setSuccess(data.message || "Office assigned source location and posted stock OUT.");
      await loadRows();
    } catch (err) {
      console.error(err);
      setError(err.message || "Assign source shop failed");
    } finally {
      setActionLoadingId("");
    }
  }

  function renderOfficeAction(row) {

    // EXTRA_ITEM_OFFICE_BUTTON_FIX_V1
    {
      const extraType = norm(row.discrepancy_type);
      const extraStatus = norm(row.status);
      const extraBusy = actionLoadingId === row.id;

      if (extraType === "EXTRA_ITEM" && extraStatus === "EXTRA_ITEM_PENDING_SENDER_DECISION") {
        return <span className="tdcNoApproval">Waiting Sender Decision</span>;
      }

      if (canOfficeAssignExtraSource(row)) {
        const selected = assignSourceMap[row.id] || "";
        return (
          <div className="tdcAssignSourceWrap">
            <select
              className="tdcAssignSourceSelect"
              value={selected}
              disabled={extraBusy}
              onChange={(e) =>
                setAssignSourceMap((prev) => ({
                  ...prev,
                  [row.id]: e.target.value,
                }))
              }
            >
              <option value="">Select source location</option>
              {shopOptions.map((shop) => (
                <option key={shop.code} value={shop.code}>
                  {shop.code}{shop.name && shop.name !== shop.code ? " - " + shop.name : ""}
                </option>
              ))}
            </select>

            <button
              type="button"
              className="tdcAssignBtn"
              disabled={extraBusy || !selected}
              onClick={() => assignExtraItemSource(row, selected)}
            >
              {extraBusy ? "Assigning..." : "Assign Source Location"}
            </button>
          </div>
        );
      }

      if (canOfficeClosedExtraItem(row)) {
        return <span className="tdcNoApproval">{statusLabel(row.status)}</span>;
      }

      if (extraType === "EXTRA_ITEM" && extraStatus === "SENDER_CONFIRMED_EXTRA_SENT") {
        return (
          <div className="tdcWrongSizeActions">
            <button
              type="button"
              className="tdcApproveBtn"
              disabled={extraBusy}
              onClick={() => approveConfirmedExtraItem(row)}
            >
              {extraBusy ? "Saving..." : "Approve Extra Item"}
            </button>

            <button
              type="button"
              className="tdcReturnBtn"
              disabled={extraBusy}
              onClick={() => rejectExtraItemClose(row)}
            >
              {extraBusy ? "Saving..." : "Reject / Close"}
            </button>
          </div>
        );
      }

      if (
        extraType === "EXTRA_ITEM" &&
        [
          "OPEN",
          "NEED_OFFICE_REVIEW",
          "EXTRA_ITEM_REPORTED",
          "OFFICE_REVIEW_PENDING",
          "SENDER_CONFIRM_PENDING",
          "EXTRA_ITEM_PENDING_SENDER_DECISION"
        ].includes(extraStatus)
      ) {
        return <span className="tdcNoApproval">Waiting Sender Decision</span>;
      }

      if (extraType === "EXTRA_ITEM" && extraStatus === "SENDER_REJECTED_EXTRA_ITEM") {
        return (
          <button
            type="button"
            className="tdcReturnBtn"
            disabled={extraBusy}
            onClick={() => rejectExtraItemClose(row)}
          >
            {extraBusy ? "Saving..." : "Reject / Close"}
          </button>
        );
      }

      if (
        extraType === "EXTRA_ITEM" &&
        [
          "EXTRA_ITEM_APPROVED_STOCK_POSTED",
          "EXTRA_ITEM_REJECTED_CLOSED",
          "EXTRA_ITEM_CLOSED_BY_SENDER_APPROVAL",
          "EXTRA_ITEM_CLOSED_BY_OFFICE_ASSIGN"
        ].includes(extraStatus)
      ) {
        return <span className="tdcNoApproval">{statusLabel(row.status)}</span>;
      }
    }


    const isBusy = actionLoadingId === row.id;
    const rowType = norm(row.discrepancy_type);

    if (canApproveExtraKeep(row)) {
      return (
        <button
          type="button"
          className="tdcApproveBtn"
          disabled={isBusy}
          onClick={() => approveExtraKeep(row)}
        >
          {isBusy ? "Approving..." : "Approve Keep"}
        </button>
      );
    }

    if (canApproveWrongItemKeep(row)) {
      return (
        <button
          type="button"
          className="tdcWrongItemApproveBtn"
          disabled={isBusy}
          onClick={() => approveWrongItemKeep(row)}
        >
          {isBusy ? "Approving..." : "Approve Wrong Item"}
        </button>
      );
    }

    if (canDecideWrongItemReturn(row)) {
      return (
        <div className="tdcWrongSizeActions">
          <button
            type="button"
            className="tdcWrongReturnBtn"
            disabled={isBusy}
            onClick={() => decideWrongItemReturn(row, "APPROVE_RETURN")}
          >
            {isBusy ? "Saving..." : "Approve Return"}
          </button>

          <button
            type="button"
            className="tdcWrongKeepBtn"
            disabled={isBusy}
            onClick={() => decideWrongItemReturn(row, "REJECT_RETURN_KEEP_RECEIVER")}
          >
            {isBusy ? "Saving..." : "Reject Return / Keep"}
          </button>
        </div>
      );
    }

    if (isWrongItemReturnWaiting(row)) {
      return (
        <span className="tdcNoApproval">
          {statusLabel(row.status)}
        </span>
      );
    }

    if (isWrongItemReturnClosed(row)) {
      return (
        <span className="tdcNoApproval">
          {statusLabel(row.status)}
        </span>
      );
    }

    if (canReturnLater(row)) {
      return (
        <button
          type="button"
          className="tdcReturnBtn"
          disabled={isBusy}
          onClick={() => markReturnSent(row)}
        >
          {isBusy ? "Saving..." : "Return Sent"}
        </button>
      );
    }

    if (canConfirmReturn(row)) {
      return (
        <button
          type="button"
          className="tdcReceiveBtn"
          disabled={isBusy}
          onClick={() => confirmReturnReceived(row)}
        >
          {isBusy ? "Saving..." : "Confirm Received"}
        </button>
      );
    }

    if (rowType === "WRONG_SIZE") {
      if (canDecideWrongSize(row)) {
        return (
          <div className="tdcWrongSizeActions">
            <button
              type="button"
              className="tdcWrongKeepBtn"
              disabled={isBusy}
              onClick={() => decideWrongSize(row, "APPROVE_KEEP_WRONG_SIZE")}
            >
              {isBusy ? "Saving..." : "Approve Keep"}
            </button>

            <button
              type="button"
              className="tdcWrongReturnBtn"
              disabled={isBusy}
              onClick={() => decideWrongSize(row, "RETURN_TO_SENDER")}
            >
              Return Sender
            </button>

            <button
              type="button"
              className="tdcWrongCenterBtn"
              disabled={isBusy}
              onClick={() => decideWrongSize(row, "RETURN_TO_CENTER")}
            >
              Return Center
            </button>

            <button
              type="button"
              className="tdcWrongWarehouseBtn"
              disabled={isBusy}
              onClick={() => decideWrongSize(row, "RETURN_TO_WAREHOUSE")}
            >
              Return Warehouse
            </button>
          </div>
        );
      }

      if (canWrongSizeReturnSent(row)) {
        return (
          <button
            type="button"
            className="tdcWrongReturnSentBtn"
            disabled={isBusy}
            onClick={() => markWrongSizeReturnSent(row)}
          >
            {isBusy ? "Saving..." : "Return Sent"}
          </button>
        );
      }

      if (canWrongSizeConfirmReceived(row)) {
        return (
          <button
            type="button"
            className="tdcWrongReceiveBtn"
            disabled={isBusy}
            onClick={() => confirmWrongSizeReturnReceived(row)}
          >
            {isBusy ? "Saving..." : "Confirm Received"}
          </button>
        );
      }

      return (
        <span className="tdcNoApproval">
          Decision completed
        </span>
      );
    }

    return <span className="tdcNoApproval">No approval</span>;
  }

  return (
    <div className="tdcPage">
      <section className="tdcHeader">
        <div>
          <span className="tdcMiniBadge">OFFICE CONTROL</span>
          <h2>Transfer Error Control</h2>
          <p>
            Review transfer mistakes, wrong size cases, keep requests, return-later cases,
            and stock approval actions.
          </p>
        </div>

        <button type="button" className="tdcRefreshBtn" onClick={loadRows} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </section>

      <section className="tdcStats">
        <div className="tdcStatCard">
          <span>Loaded</span>
          <b>{stats.loaded}</b>
        </div>
        <div className="tdcStatCard tdcStatOpen">
          <span>Open</span>
          <b>{stats.open}</b>
        </div>
        <div className="tdcStatCard tdcStatReview">
          <span>Need Review</span>
          <b>{stats.needReview}</b>
        </div>
        <div className="tdcStatCard tdcStatWrong">
          <span>Wrong Size</span>
          <b>{stats.wrongSize}</b>
        </div>
        <div className="tdcStatCard tdcStatKeep">
          <span>Keep Approval</span>
          <b>{stats.keepApproval}</b>
        </div>
      </section>

      <section className="tdcFilters">
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUS_OPTIONS.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>

        <select value={type} onChange={(e) => setType(e.target.value)}>
          {TYPE_OPTIONS.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>

        <input
          value={shop}
          onChange={(e) => setShop(e.target.value.toUpperCase())}
          placeholder="ALL / GMP / DNGPF"
        />

        <button type="button" onClick={loadRows} disabled={loading}>
          Search
        </button>
      </section>

      {error && <div className="tdcAlert tdcAlertError">{error}</div>}
      {success && <div className="tdcAlert tdcAlertSuccess">{success}</div>}

      <section className="tdcTableWrap">
        <table className="tdcTable">
          <thead>
            <tr>
              <th>Report</th>
              <th>Route</th>
              <th>Issue</th>
              <th>Expected</th>
              <th>Received</th>
              <th>Status</th>
              <th>Office</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan="7" className="tdcEmpty">
                  No transfer error records found.
                </td>
              </tr>
            )}

            {rows.map((row) => {
              const rowId = row.id;
              const isExpanded = expandedId === rowId;
              const rowType = norm(row.discrepancy_type);

              return (
                <React.Fragment key={rowId}>
                  <tr>
                    <td>
                      <b>{row.voucher_no || "-"}</b>
                      <small>{formatDate(row.created_at)}</small>
                      <small>ID: {shortId(row.id)}</small>
                    </td>

                    <td>
                      <span className="tdcRoute">
                        {row.from_location_code || "-"} → {row.to_location_code || "-"}
                      </span>
                      <small>Reported by {row.reported_by_location || "-"}</small>
                    </td>

                    <td>
                      <span className={`tdcIssuePill tdcIssue_${rowType}`}>
                        {typeLabel(row.discrepancy_type)}
                      </span>
                      <small>{actionLabel(row.requested_action)}</small>
                      {row.return_destination && (
                        <small>Destination: {row.return_destination}</small>
                      )}
                    </td>

                    <td className="tdcSkuText">{getExpectedText(row)}</td>
                    <td className="tdcSkuText">{getReceivedText(row)}</td>

                    <td>
                      <span className={`tdcStatusPill tdcStatus_${norm(row.status)}`}>
                        {statusLabel(row.status)}
                      </span>
                      <small>{row.resolution_type || "-"}</small>
                    </td>

                    <td>
                      <div className="tdcOfficeCell">
                        <button
                          type="button"
                          className="tdcDetailBtn"
                          onClick={() => setExpandedId(isExpanded ? "" : rowId)}
                        >
                          Detail
                        </button>

                        {renderOfficeAction(row)}
                      </div>
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr>
                      <td colSpan="7">
                        <div className="tdcDetailBox">
                          <div>
                            <b>Reason</b>
                            <p>{row.reason || "-"}</p>
                          </div>

                          <div>
                            <b>Delivery Staff</b>
                            <p>{row.delivery_staff || "-"}</p>
                          </div>

                          <div>
                            <b>Requested Action</b>
                            <p>{actionLabel(row.requested_action)}</p>
                          </div>

                          <div>
                            <b>Return Destination</b>
                            <p>{row.return_destination || "-"}</p>
                          </div>

                          <div>
                            <b>Office Decision</b>
                            <p>{row.office_decision || "Waiting office decision"}</p>
                          </div>

                          <div>
                            <b>Return Carrier / Delivery</b>
                            <p>{row.return_carrier || row.delivery_staff || "-"}</p>
                          </div>

                          <div>
                            <b>Return Sent Time</b>
                            <p>{formatDate(row.return_sent_at)}</p>
                          </div>

                          <div>
                            <b>Return Received Time</b>
                            <p>{formatDate(row.return_received_at)}</p>
                          </div>

                          <div>
                            <b>Responsible</b>
                            <p>{row.responsible_area || "-"} / {row.responsible_location || "-"}</p>
                          </div>

                          <div>
                            <b>Note</b>
                            <p>{row.note || "-"}</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="tdcRuleBox">
        <b>Stock rule:</b> Wrong Item return flow supports Office approve return,
        reject return and keep at receiver, delivery assignment, return sent, and sender received close.
        Inventory is posted only by approved decision steps.
      </section>
    </div>
  );
}
